// frontend/src/components/layout/ChatNavbarIcon.js
import React, { useState, useRef, useMemo } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItem,
  ListItemAvatar, ListItemText, Avatar, Divider, Button,
  ListItemButton, Chip, Tooltip
} from '@mui/material';
import {
  Chat as ChatIcon, People as GroupIcon,
  Check as CheckIcon, DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const THEME = {
  primary: '#667eea',
  primaryLight: 'rgba(102, 126, 238, 0.12)',
  primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  unreadBg: 'rgba(102, 126, 238, 0.08)',
  unreadDot: '#ff6b6b',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  border: '#e2e8f0',
  hover: 'rgba(0, 0, 0, 0.04)',
  online: '#22c55e',
};

const ChatNavbarIcon = () => {
  const { conversations, unreadTotal, open, setOpen, loadMessages, markAsRead } = useChat();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // ✅ FIX: Only open ChatWindow (via loadMessages), NOT ChatDrawer
  const handleConversationClick = (conversationId) => {
    loadMessages(conversationId);
    markAsRead?.(conversationId);
    handleClose();
  };

  const isOpen = Boolean(anchorEl);

  const getAvatar = (avatar, isOnline = false) => {
    const getEnvVar = (key) => {
      if (typeof import.meta !== 'undefined' && import.meta.env) return import.meta.env[key];
      if (typeof process !== 'undefined' && process.env) return process.env[key];
      return null;
    };
    const baseUrl = getEnvVar('VITE_AVATAR_URL') || getEnvVar('REACT_APP_AVATAR_URL') || 'http://localhost:8000/storage';
    const avatarUrl = avatar ? `${baseUrl}/${avatar}` : null;
    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Avatar src={avatarUrl} sx={{ width: 44, height: 44, bgcolor: THEME.primary, fontSize: '1rem', border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {!avatarUrl && ''}
        </Avatar>
        {isOnline && <Box sx={{ position: 'absolute', bottom: 2, right: 2, width: 12, height: 12, bgcolor: THEME.online, borderRadius: '50%', border: '2px solid white' }} />}
      </Box>
    );
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const isToday = date.toDateString() === new Date().toDateString();
    return isToday ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : formatDistanceToNow(date, { addSuffix: true });
  };

  const truncateMessage = (text, maxLength = 35) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;
  };

  const recentConversations = useMemo(() => conversations.slice(0, 5), [conversations]);

  return (
    <>
      <Tooltip title="Messages" arrow placement="bottom">
        <IconButton onClick={handleClick} sx={{ position: 'relative', transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }} aria-label="open chat notifications" aria-controls={isOpen ? 'chat-popover' : undefined} aria-expanded={isOpen ? 'true' : undefined} aria-haspopup="true">
          <Badge badgeContent={unreadTotal > 0 ? unreadTotal : null} color="error" sx={{ '& .MuiBadge-badge': { right: 8, top: 4, fontSize: '0.7rem', minWidth: '18px', height: '18px', padding: '4px 6px', animation: unreadTotal > 0 ? 'pulse 2s infinite' : 'none', '@keyframes pulse': { '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' }, '70%': { boxShadow: '0 0 0 8px rgba(244, 67, 54, 0)' }, '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' } } } }}>
            <ChatIcon sx={{ color: "#475569", fontSize: 26, transition: 'color 0.2s ease', "&:hover": { color: THEME.primary } }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover id="chat-popover" open={isOpen} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} disableRestoreFocus PaperProps={{ sx: { width: 360, maxWidth: '95vw', maxHeight: 480, borderRadius: 3, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)', border: `1px solid ${THEME.border}`, overflow: 'hidden', mt: 1.5, '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: THEME.primaryGradient } } }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color={THEME.textPrimary}>Messages</Typography>
            <Typography variant="caption" color={THEME.textSecondary}>{unreadTotal > 0 ? `${unreadTotal} unread` : 'All caught up'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Mark all as read">
              <IconButton size="small" sx={{ color: THEME.textSecondary }} onClick={() => conversations.forEach(c => c.unread_count > 0 && markAsRead?.(c.id))}><DoneAllIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Button size="small" variant="contained" onClick={() => { setOpen(true); handleClose(); }} sx={{ bgcolor: THEME.primary, '&:hover': { bgcolor: '#5a6fd6' }, textTransform: 'none', fontSize: '0.8rem', px: 2, minHeight: 32 }}>Open Chat</Button>
          </Box>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <List sx={{ p: 0.5, maxHeight: 340, overflow: 'auto', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: '3px', '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' } } }}>
          {recentConversations.length > 0 ? (
            recentConversations.map((c, index) => {
              const isUnread = c.unread_count > 0;
              const otherUser = c.users?.[0];
              const isGroup = c.type === 'group';
              return (
                <React.Fragment key={c.id}>
                  <ListItemButton onClick={() => handleConversationClick(c.id)} sx={{ py: 1.5, px: 1.5, borderRadius: 2, mx: 0.5, my: 0.25, bgcolor: isUnread ? THEME.unreadBg : 'transparent', borderLeft: isUnread ? `3px solid ${THEME.primary}` : '3px solid transparent', transition: 'all 0.15s ease', '&:hover': { bgcolor: THEME.hover, transform: 'translateX(2px)' }, '&:active': { transform: 'scale(0.995)' } }}>
                    <ListItemAvatar sx={{ minWidth: 56 }}>{getAvatar(otherUser?.avatar, otherUser?.is_online)}</ListItemAvatar>
                    <ListItemText
                      primary={<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}><Typography variant="subtitle2" fontWeight={isUnread ? 700 : 600} color={isUnread ? THEME.textPrimary : THEME.textSecondary} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '65%' }}>{isGroup && <GroupIcon fontSize="small" sx={{ color: THEME.primary }} />}{truncateMessage(isGroup ? c.name : otherUser?.name, 20)}</Typography>{c.last_message?.created_at && <Typography variant="caption" color={THEME.textSecondary} sx={{ flexShrink: 0 }}>{formatMessageTime(c.last_message.created_at)}</Typography>}</Box>}
                      secondary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{isUnread && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: THEME.unreadDot, flexShrink: 0 }} />}<Typography variant="body2" color={THEME.textSecondary} sx={{ lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>{c.last_message?.body ? truncateMessage(c.last_message.body) : c.last_message?.file_name ? `📎 ${c.last_message.file_name}` : 'Start the conversation 👋'}</Typography>{c.last_message?.sender_id === user?.id && <CheckIcon sx={{ fontSize: 16, color: THEME.textSecondary, flexShrink: 0 }} />}</Box>}
                      primaryTypographyProps={{ component: 'div' }} secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                  {index < recentConversations.length - 1 && <Divider sx={{ mx: 2, opacity: 0.5 }} component="li" />}
                </React.Fragment>
              );
            })
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: THEME.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}><ChatIcon sx={{ color: THEME.primary, fontSize: 28 }} /></Box>
              <Typography variant="subtitle2" fontWeight={600} color={THEME.textPrimary}>No conversations yet</Typography>
              <Typography variant="body2" color={THEME.textSecondary} sx={{ mt: 0.5 }}>Start chatting with your team!</Typography>
              <Button variant="outlined" size="small" onClick={() => { setOpen(true); handleClose(); }} sx={{ mt: 2, borderColor: THEME.primary, color: THEME.primary, '&:hover': { borderColor: THEME.primary, bgcolor: THEME.primaryLight } }}>Start a Chat</Button>
            </Box>
          )}
        </List>
        {conversations.length > 5 && (<><Divider /><Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}><Button size="small" onClick={() => { setOpen(true); handleClose(); }} sx={{ color: THEME.primary, fontWeight: 500, textTransform: 'none', '&:hover': { bgcolor: THEME.primaryLight } }}>View all {conversations.length} conversations →</Button></Box></>)}
      </Popover>
    </>
  );
};

export default ChatNavbarIcon;